import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import { Link, useParams } from "react-router-dom";
import { Browser } from "@capacitor/browser";
import { yupResolver } from "@hookform/resolvers/yup";
import { mdiLoading } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";
import { useToggle } from "usehooks-ts";
import * as Yup from "yup";

import Button from "components/Button";
import Checkbox from "components/Checkbox";
import ConfirmationModal from "components/ConfirmationModal";
import GoogleAutoComplete from "components/GoogleAutoComplete";
import Input from "components/Input";
import PhoneNumber from "components/PhoneNumber";
import Select from "components/Select";
import Switch from "components/Switch";
import { BUSINESS_TYPE_LIST } from "constants/haulpay";
import { routes } from "constants/routes";
import { TERMS_OF_SERVICE, VEHICLE_NUMBER_TYPES } from "constants/signup";
import { RESPONSE_STATUS } from "constants/status";
import { phoneRegex } from "helpers/regex";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import useDeviceType from "hooks/useDeviceType";
import { IOption } from "models/Form";
import { AddressInfo, IUserInfoForm } from "models/User";
import { checkEmailExistAPI, getUserByFmcsaAPI, signupAPI } from "services/api/user";
import { AppDispatch, RootState } from "store";
import { loginUser } from "store/auth/actions";
import { createFactoringUser } from "store/user/actions";
import styles from "./style.module.scss";

const createInfoFormSchema = (isApplyHaulpay: boolean) =>
  Yup.object<Partial<IUserInfoForm>>({
    mc_dot: Yup.string().required("This field is required"),
    business_type: Yup.object<IOption>().when([], {
      is: () => isApplyHaulpay,
      then: (schema) => schema.required("This field is required"),
      otherwise: (schema) => schema.notRequired()
    }),
    email: Yup.string()
      .email("This field value must be a valid email")
      .required("This field is required"),
    password: Yup.string().when([], {
      is: () => isApplyHaulpay,
      then: (schema) =>
        schema.required("Password is required").min(8, "Password must be at least 8 characters"),
      otherwise: (schema) => schema.notRequired()
    }),
    owners_first_name: Yup.string().required("This field is required"),
    owners_last_name: Yup.string().required("This field is required"),
    contact_phone: Yup.string()
      .required("This field is required")
      .max(19, "Phone number limit exceeded")
      .matches(phoneRegex, { message: "Phone number is not valid", excludeEmptyString: true }),
    dba: Yup.string().required("This field is required"),
    address_object: Yup.object().required("This field is required"),
    agreement: Yup.bool().when([], {
      is: () => isApplyHaulpay,
      then: (schema) => schema.oneOf([true], "You must accept the terms of service"),
      otherwise: (schema) => schema.notRequired()
    })
  });

interface IProps {
  submitData: IUserInfoForm & { factoring_id?: string };
  onNext: (data: IUserInfoForm & { factoring_id?: string }) => void;
}

export default function JoinCreateInfo({ submitData, onNext }: Readonly<IProps>) {
  const { isDesktop } = useDeviceType();
  const { pathname } = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { userDetails } = useSelector((state: RootState) => state.userState);

  const [isLoading, setIsLoading] = useState(false);
  const [isDuplicatedEmail, setIsDuplicatedEmail] = useState(false);
  const [vehicleNumberType, setVehicleNumberType] = useState(VEHICLE_NUMBER_TYPES[0]);
  const [isConfirmModalOpen, toggleConfirmModal] = useToggle();

  const isApplyHaulpay = pathname.startsWith("/signup/");
  const validationSchema = useMemo(() => createInfoFormSchema(isApplyHaulpay), [isApplyHaulpay]);
  const location = useLocation();
  const { userType } = useParams<{ userType: "broker" | "carrier" }>();

  const {
    register,
    control,
    reset,
    watch,
    getValues,
    setValue,
    formState: { errors },
    handleSubmit
  } = useForm<Partial<IUserInfoForm>>({
    resolver: yupResolver(validationSchema)
  });
  const userEmail = watch("email");
  const businessAddress = watch("address_object");

  const checkEmailExist = async (email?: string) => {
    if (email) {
      try {
        const response = await checkEmailExistAPI(email);
        if (response.data.status === RESPONSE_STATUS.FAILURE) setIsDuplicatedEmail(true);
        else setIsDuplicatedEmail(false);
      } catch (err: any) {
        setIsDuplicatedEmail(false);
      }
    }
  };

  const getFMCSAInfo = async () => {
    try {
      const { data: fmcsaData } = await getUserByFmcsaAPI({
        [vehicleNumberType.value]: getValues("mc_dot")
      });

      if (fmcsaData) {
        const { address, city, country, state, zip, dba, phone } = fmcsaData;
        const businessAddress: AddressInfo = {
          address,
          city,
          country,
          state,
          zip
        };
        setValue("dba", dba);
        setValue("address_object", businessAddress);
        setValue("contact_phone", phone);
      }
    } catch (err) {
      setValue("dba", "");
    }
  };

  const goToTCPage = async (event: any) => {
    if (!isDesktop) {
      event.preventDefault();
      await Browser.open({ url: TERMS_OF_SERVICE });
    }
  };

  const onSubmit = async (data: IUserInfoForm) => {
    const signup_url = `${window.location.origin}${location.pathname}${location.search}${location.hash}`;
    const address_object = {
      ...data.address_object,
      address: data.address_object?.address ?? data.address_object?.street_one,
      street_one: data.address_object?.street_one ?? data.address_object?.address,
      street_two: data.street_two
    };
    const formData = {
      factoring_approved: "lead_in",
      username: data.email,
      signup_url,
      referrer_url: document?.referrer || signup_url,
      [isApplyHaulpay ? data.business_type.value : userDetails.type ?? "carrier"]: {
        contact_name: `${data.owners_first_name} ${data.owners_last_name}`,
        address_object: address_object,
        country: address_object?.country,
        state: address_object?.state,
        city: address_object?.city,
        zip: address_object?.zip,
        address: address_object?.address,
        street_two: data.street_two,
        enable_sms_alerts: true,
        mc: vehicleNumberType.value === "mc" ? data.mc_dot : "",
        dot: vehicleNumberType.value === "dot" ? data.mc_dot : "",
        contact_phone: data.contact_phone,
        dba: data.dba
      },
      owner_profile: {
        name_first: data.owners_first_name,
        name_last: data.owners_last_name,
        email: data.email,
        phone_number_cell: data.contact_phone,
        address: address_object
      },
      company_profile: {
        name: data.dba,
        address: address_object,
        phone: data.contact_phone,
        mc: vehicleNumberType.value === "mc" ? data.mc_dot : "",
        dot: vehicleNumberType.value === "dot" ? data.mc_dot : ""
      },
      type: isApplyHaulpay ? data.business_type.value : userDetails.type,
      owner_first_name: data.owners_first_name,
      owner_last_name: data.owners_last_name,
      email: data.email,
      owner_phone_number: data.contact_phone,
      mc: vehicleNumberType.value === "mc" ? data.mc_dot : "",
      dot: vehicleNumberType.value === "dot" ? data.mc_dot : "",
      company_name: data.dba,
      terms: true,
      address: address_object?.address,
      street_two: data.street_two,
      ...(isApplyHaulpay && { password: data.password }),
      ...(isApplyHaulpay && { signup_process: "factoring_only" }),
      ...(!isApplyHaulpay && { enable_sms_alerts: true })
    };
    try {
      setIsLoading(true);
      if (isApplyHaulpay) {
        await signupAPI(formData as any, { source: "Haulpay.co" });
        await dispatch(
          loginUser({
            username: data.email.toLowerCase(),
            password: data.password,
            grant_type: "password",
            client_id: "yzxsFZJYZMxi5kLh21ZZklGYzSuu9zxNO6miNcN8"
          })
        );
      }
      dispatch(createFactoringUser({ formData: formData })).then(({ payload }) => {
        showSuccessMessage("Successfully create factoring user");
        onNext({ ...data, factoring_id: payload.id });
      });
    } catch {
      showErrorMessage("Error creating factoring user");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isApplyHaulpay) {
      reset({
        mc_dot: vehicleNumberType.value === "mc" ? userDetails?.mc : userDetails?.dot,
        email: userDetails?.email,
        owners_first_name: userDetails?.contact_name?.split(" ")[0],
        owners_last_name: userDetails?.contact_name?.split(" ").slice(1).join(" "),
        contact_phone: userDetails?.contact_phone,
        dba: userDetails?.dba,
        address_object: submitData
          ? submitData?.address_object
          : {
              ...userDetails?.address_object,
              address: userDetails?.address_object?.street_one
            }
      });
    } else if (userType) {
      reset({
        business_type:
          userType === BUSINESS_TYPE_LIST[0].value ? BUSINESS_TYPE_LIST[0] : BUSINESS_TYPE_LIST[1]
      });
    }
  }, []);

  return (
    <form
      className={clsx(styles.carrierInfoContainer, !isApplyHaulpay && "mobile:pb-24")}
      onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.header}>
        <h3 className="text-xl font-medium">Create Info</h3>
        <span className="text-sm">
          Please make sure to confirm all of the following information.
        </span>
      </div>

      <div className={styles.wrapper}>
        {isApplyHaulpay ? (
          <Controller
            name="business_type"
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Select
                label="&nbsp;&nbsp;Business Type"
                value={value}
                options={BUSINESS_TYPE_LIST}
                onChange={onChange}
                error={error}
                placeholder="What type of Business Are You?"
                disabled={Boolean(userType)}
              />
            )}
          />
        ) : null}

        <div className={clsx(styles.enterMC, isApplyHaulpay ? "flex-1" : "desktop:col-span-2")}>
          <Input
            type="number"
            label={vehicleNumberType.value === "mc" ? "MC#" : "DOT#"}
            inputMode="numeric"
            name="mc_dot"
            register={register}
            error={errors.mc_dot}
            inputClass="!border-foreground h-12"
            className="flex-grow"
            onBlur={getFMCSAInfo}
          />
          <div className="flex items-center pt-5">
            <Switch data={VEHICLE_NUMBER_TYPES} setSelectedValue={setVehicleNumberType} />
          </div>
        </div>

        <div className={clsx(styles.emailField, isApplyHaulpay ? "flex-1" : "desktop:col-span-2")}>
          <Input
            autoComplete="off"
            name="email"
            register={register}
            error={errors.email}
            type="email"
            label="&nbsp;&nbsp;Email"
            onBlur={() => checkEmailExist(userEmail)}
          />
          {isDuplicatedEmail && (
            <span className="text-sm text-error">
              This email already exists. Please{" "}
              <Link to={`${routes.SIGNIN}/?redirection_url=join`} className={styles.emailError}>
                Click Here
              </Link>{" "}
              to login and complete
            </span>
          )}
        </div>

        {isApplyHaulpay ? (
          <Input
            name="password"
            label="Password"
            register={register}
            error={errors.password}
            isPasswordIcon
          />
        ) : null}

        <div className={styles.fieldGroup}>
          <Input
            label="&nbsp;&nbsp;Owner's First Name"
            name="owners_first_name"
            register={register}
            error={errors.owners_first_name}
            className="flex-1"
          />
          <Input
            label="&nbsp;&nbsp;Owner's Last Name"
            name="owners_last_name"
            register={register}
            error={errors.owners_last_name}
            className="flex-1"
          />
        </div>
        <Controller
          name="contact_phone"
          control={control}
          render={({ field: { value = "", onChange } }) => (
            <PhoneNumber
              value={value}
              onChange={onChange}
              label="&nbsp;&nbsp;Owner's Phone#"
              error={errors.contact_phone}
              wrapperClass="desktop:col-span-2 flex-1"
            />
          )}
        />

        <Input label="&nbsp;&nbsp;Company Name" name="dba" register={register} error={errors.dba} />

        <div className={styles.fieldGroup}>
          <Controller
            name="address_object"
            control={control}
            render={({ field: { onChange }, fieldState: { error } }) => (
              <GoogleAutoComplete
                label="Business Address"
                error={error}
                value={businessAddress}
                onChange={(addressInfo: AddressInfo) => onChange(addressInfo)}
                containerClass="flex-1"
              />
            )}
          />
          <Input
            name="street_two"
            register={register}
            label="&nbsp;&nbsp;Street 2"
            className="flex-1"
          />
        </div>

        <div className={styles.agreementCheckbox}>
          {isApplyHaulpay ? (
            <Checkbox
              name="agreement"
              register={register}
              error={errors.agreement}
              wrapperClass="mobile:mt-3">
              I agree to the HaulPay{" "}
              <a
                href={TERMS_OF_SERVICE}
                target="blank"
                onClick={goToTCPage}
                className="underline text-primary">
                Terms of Service
              </a>
            </Checkbox>
          ) : null}

          <Checkbox name="enableSmsAlert" register={register} wrapperClass="desktop:col-span-2">
            I would like to receive{" "}
            <button type="button" onClick={toggleConfirmModal} className={styles.checkbox}>
              important text messages regarding my operations
            </button>
          </Checkbox>
        </div>
      </div>

      <div className={styles.footer}>
        <Button type="submit" size="lg" loading={isLoading}>
          {isLoading ? <Icon path={mdiLoading} size={1} spin={1} /> : "NEXT"}
        </Button>
      </div>

      <ConfirmationModal
        title="Comfreight Text Message"
        isConfirmModalOpen={isConfirmModalOpen}
        toggleConfirmModal={toggleConfirmModal}
        isHideHeaderIcon
        isHideCancelButton
        content={
          <div className={styles.confirmationModalContainer}>
            <p>
              If enabled, we’ll send you a text(SMS) when there are urgent matters that need your
              attention, such as:
            </p>

            <ul className={styles.wrapper}>
              <li>There are document issues with a funding request</li>
              <li>You are underbid on a bid</li>
              <li>And others</li>
            </ul>

            <p>
              Depending on your activity on our platform, you can expect between 1 text every month
              to 1 text per day.
            </p>
            <p>Charges may apply.</p>
          </div>
        }
      />
    </form>
  );
}
